import slugify from '@sindresorhus/slugify';

import { isDefined, isNil, TMaybe } from './maybe';

type TWithoutString<TPType> = TPType extends string ? never : TPType;
type TJustString<TPType> = TPType extends string ? TPType : never;

function isString(value: unknown): value is string {
  return isDefined(value) && typeof value === 'string';
}

function isEmptyString(value: string): value is string {
  return value.length === 0;
}

function isNotEmptyString(value: unknown): value is string {
  return isString(value) && value.length > 0;
}

function isBlank(value?: string): boolean {
  return isNil(value) || value.trim() === '';
}

type TAnonymizeOptions = {
  slice?: number;
  replacementChar?: string;
  showLast?: number;
};

function anonymize(value: TMaybe<string>, options: TAnonymizeOptions = {}): string {
  const { slice, replacementChar = '*', showLast } = options;

  if (!isDefined(value)) {
    return '';
  }

  if (isDefined(slice) && isDefined(showLast)) {
    throw new Error('Anonymize options "slice" and "showLast" cannot be used together.');
  }

  if (isDefined(slice)) {
    value = value.slice(0, slice);
  }

  if (isDefined(showLast)) {
    const start = Math.max(0, value.length - showLast);
    const end = value.length;

    return replacementChar.repeat(start) + value.slice(start, end);
  }

  return value.replace(/./g, replacementChar);
}

function getJustString<TPType, TPFallback>(
  value: TPType,
  orElse: (value: TWithoutString<TPType>) => TPFallback
): TJustString<TPType> | TPFallback;
function getJustString<TPType, TPFallback>(
  value: TPType,
  orElse: (value: TWithoutString<TPType>) => Promise<TPFallback>
): Promise<TJustString<TPType> | TPFallback>;
function getJustString<TPType, TPFallback>(
  value: TPType,
  orElse: (value: TWithoutString<TPType>) => TPFallback | Promise<TPFallback>
): TJustString<TPType> | TPFallback | Promise<TJustString<TPType> | TPFallback> {
  return isString(value) ? (value as TJustString<TPType>) : orElse(value as TWithoutString<TPType>);
}

function padLeft(value: string, length: number, char: string = '0'): string {
  return value.padStart(length, char);
}

function padRight(value: string, length: number, char: string = '0'): string {
  return value.padEnd(length, char);
}

function firstUpper(value: string): string {
  return value.charAt(0).toUpperCase() + value.slice(1);
}

function firstLower(value: string): string {
  return value.charAt(0).toLowerCase() + value.slice(1);
}

function lowerCase(value: string): string {
  return value.toLowerCase();
}

function upperCase(value: string): string {
  return value.toUpperCase();
}

function toString(value: unknown): string {
  if (isDefined(value)) {
    return String(value);
  }

  return '';
}

type TSnakeCase<TPValue extends string> = TPValue extends `${infer T}${infer U}`
  ? `${T extends Capitalize<T> ? '_' : ''}${Lowercase<T>}${TSnakeCase<U>}`
  : TPValue;

function camelCase<TPValue extends string>(value: TPValue): TSnakeCase<TPValue> {
  return value.replace(/([-_][a-z])/gi, ($1) => {
    return $1.toUpperCase().replace('-', '').replace('_', '');
  }) as TSnakeCase<TPValue>;
}

type TCamelCase<TPValue extends string> = TPValue extends `${infer T}_${infer U}`
  ? `${T}${Capitalize<TCamelCase<U>>}`
  : TPValue;

function snakeCase<TPValue extends string>(value: TPValue): TCamelCase<TPValue> {
  return kebabCase(value).replace(/-/g, '_') as TCamelCase<TPValue>;
}

type TKebabCase<TPValue extends string> = TPValue extends `${infer T}${infer U}`
  ? `${T extends Capitalize<T> ? '-' : ''}${Lowercase<T>}${TKebabCase<U>}`
  : TPValue;

function kebabCase<TPValue extends string>(value: TPValue): TKebabCase<TPValue> {
  return value
    .replace(/([a-z])([A-Z])/g, '$1-$2')
    .replace(/[\s_]+/g, '-')
    .replace(/[^\w-]+/g, '') // Remove special characters
    .toLowerCase() as TKebabCase<TPValue>;
}

function divider(character: string = '-', length: number = 80): string {
  return character.repeat(length);
}

function pluralize(word: string): string {
  if (/.*(o|ch|s|sh|x|z)$/.test(word)) {
    return word + 'es';
  } else if (/.*[^aeiou]y$/.test(word)) {
    return word.replace(/y$/, 'ies');
  } else if (/\s/.test(word)) {
    const words = word.split(' ');
    words[words.length - 1] = pluralize(words[words.length - 1]);
    return words.join(' ');
  } else {
    return word + 's';
  }
}

function randomString(length: number): string {
  const characters = 'abcdefghijklmnopqrstuvwxyzABCDEFGHIJKLMNOPQRSTUVWXYZ0123456789 ';
  let result = '';

  for (let i = 0; i < length; i++) {
    result += characters.charAt(Math.floor(Math.random() * characters.length));
  }

  return result;
}

export {
  TWithoutString,
  TJustString,
  TCamelCase,
  TSnakeCase,
  isString,
  isEmptyString,
  isNotEmptyString,
  isBlank,
  anonymize,
  getJustString,
  padLeft,
  padRight,
  firstUpper,
  firstLower,
  lowerCase,
  upperCase,
  toString,
  camelCase,
  snakeCase,
  kebabCase,
  divider,
  slugify,
  pluralize,
  randomString,
};
